import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500/50 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98]",
  {
    variants: {
      variant: {
        default: "bg-purple-500 text-white hover:bg-purple-600 shadow-lg shadow-purple-500/25",
        destructive: "bg-rose-500 text-white hover:bg-rose-600 shadow-lg shadow-rose-500/25",
        outline: "border border-light-border dark:border-dark-border bg-transparent hover:bg-light-elevated dark:hover:bg-dark-elevated text-light-text dark:text-dark-text",
        secondary: "bg-light-elevated dark:bg-dark-elevated text-light-text dark:text-dark-text hover:bg-light-border dark:hover:bg-dark-border/50",
        ghost: "hover:bg-light-elevated dark:hover:bg-dark-elevated text-light-text-secondary dark:text-dark-text-secondary",
        link: "text-purple-500 underline-offset-4 hover:underline",
        gradient: "text-white shadow-lg",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-8 rounded-lg px-3 text-xs",
        lg: "h-12 rounded-xl px-8 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
